import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveDemoLauncher = ({ dashboards, onClose }) => {
  const navigate = useNavigate();
  const [selectedDashboard, setSelectedDashboard] = useState(dashboards?.[0]);

  const sampleCompanyData = {
    companyName: 'PNG Mining Corp (Demo)',
    employees: 247,
    departments: ['Operations', 'Administration', 'Finance', 'HR', 'Safety'],
    complianceScore: 94,
    currency: 'PGK (Kina)',
    regulations: ['Employment Act 1978', 'NASFUND', 'SWT', 'IRC Tax']
  };

  const handleLaunchDemo = () => {
    navigate(selectedDashboard?.path);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-1000 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-lg">
              <Icon name="Rocket" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground">Launch Interactive Demo</h2>
              <p className="text-sm text-muted-foreground">Explore with real PNG company sample data</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dashboard Selection */}
            <div>
              <h3 className="font-semibold text-foreground mb-3">Select Dashboard</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {dashboards?.map((dashboard) => (
                  <button
                    key={dashboard?.id}
                    onClick={() => setSelectedDashboard(dashboard)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      selectedDashboard?.id === dashboard?.id
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                    }`}
                  >
                    <div className={`p-2 rounded-md ${
                      selectedDashboard?.id === dashboard?.id
                        ? 'bg-primary/10 text-primary' :'bg-muted text-muted-foreground'
                    }`}>
                      <Icon name={dashboard?.icon} size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground truncate">
                        {dashboard?.name}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate">
                        {dashboard?.features?.[0]}
                      </p>
                    </div>
                    {selectedDashboard?.id === dashboard?.id && (
                      <Icon name="CheckCircle" size={18} className="text-primary flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Sample Data Preview */}
            <div>
              <h3 className="font-semibold text-foreground mb-3">Sample Company Data</h3>
              <div className="bg-muted/50 border border-border rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Icon name="Building2" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">{sampleCompanyData?.companyName}</h4>
                    <p className="text-xs text-muted-foreground">Demo Environment</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Total Employees</span>
                    <span className="data-text font-semibold text-foreground">{sampleCompanyData?.employees}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Departments</span>
                    <span className="data-text font-semibold text-foreground">{sampleCompanyData?.departments?.length}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Compliance Score</span>
                    <span className="data-text font-semibold text-success">{sampleCompanyData?.complianceScore}%</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-muted-foreground">Currency</span>
                    <span className="data-text font-semibold text-foreground">{sampleCompanyData?.currency}</span>
                  </div>
                </div>
              </div>

              {/* Regulations Covered */}
              <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                <h4 className="caption text-xs font-semibold text-foreground mb-3 uppercase tracking-wide">
                  PNG Regulations Covered
                </h4>
                <div className="flex flex-wrap gap-2">
                  {sampleCompanyData?.regulations?.map((reg, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-success/10 text-success text-xs rounded-md font-medium"
                    >
                      {reg}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Selected Dashboard Preview */}
          {selectedDashboard && (
            <div className="mt-6 pt-6 border-t border-border">
              <h3 className="font-semibold text-foreground mb-3">What You'll Explore</h3>
              <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-lg p-5">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Icon name={selectedDashboard?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-heading text-lg font-bold text-foreground mb-2">
                      {selectedDashboard?.name}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      {selectedDashboard?.description}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {selectedDashboard?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>Demo uses sample PNG company data</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="default" 
              iconName="Rocket"
              onClick={handleLaunchDemo}
            >
              Launch {selectedDashboard?.name}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveDemoLauncher;